import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import Link from 'next/link';
import TravelWitsLogo from '../../../assets/svgs/travelwitsLogo.svg';
import AgencyLogo from '../../AgencyLogo';
import NavbarDropdown from './NavbarDropdown';
import { checkIsLoggedIn, getSiteHeaderVariant, getTravelAgency } from '../../../store/slices/user';
import HeaderVariant from '../../../constants/HeaderVariant';
import NavbarHelpDropdown from './NavbarHelpDropdown';
import SelectCurrency from '../../SelectCurrency';
import AgencyLogoWithAttribution from '../../AgencyLogoWithAttribution';
import NavbarMenu from './NavbarMenu';
import { logoLayoutVariants, TwAttributionVariants } from '../../../constants';
import MarkdownToHtml from '../../MarkdownToHtml';
import CustomHeaderLinks from './CustomHeaderLinks';
import UserLocalPreferences from './UserLocalPreferences';

const Navbar = ({ agencyLogoOverride }) => {
  const { isLoading } = useAuth0();
  const isLoggedIn = useSelector(checkIsLoggedIn);
  const travelAgency = useSelector(getTravelAgency);
  const headerVariant = useSelector(getSiteHeaderVariant);
  const affiliateTextLine = travelAgency && travelAgency.settings
    && travelAgency.settings.affiliateText;
  const affiliateTextUnderAdditionalLogo = travelAgency && travelAgency.settings
    && travelAgency.settings.affiliateTextUnderAdditionalLogo;
  const doAlignCurrencyFilterWithOtherFilters = !!(travelAgency && travelAgency.filterSettings
    && travelAgency.filterSettings.doAlignCurrencyFilterWithOtherFilters);
  const doShowWithBranding = !isLoading && headerVariant === HeaderVariant.withBranding;
  const doShowWithBrandingTopBar = !isLoading && headerVariant === HeaderVariant.withBrandingTopBar;
  const twAttributionVariant = travelAgency && travelAgency.siteTheme
    && travelAgency.siteTheme.TwAttributionVariant;
  const doShowAttributionBelowAgencyLogo = twAttributionVariant
    === TwAttributionVariants.BELOW_AGENCY_LOGO;
  const doShowAttributionUnderSearchBar = twAttributionVariant
    === TwAttributionVariants.UNDER_SEARCH_BAR;
  const doShowAffiliateTextToFooter = travelAgency && travelAgency.settings
    && travelAgency.settings.doShowAffiliateTextToFooter;
  const doCenterAlignAdditionalLogo = travelAgency && travelAgency.settings
    && travelAgency.settings.logoLayoutVariant === logoLayoutVariants.LOGO_IMAGE_VARIANT_CENTERED;
  const agencyAdditionalLogo = travelAgency && travelAgency.settings
    && travelAgency.settings.agencyAdditionalLogo;

  const doShowUserLocalPreferences = !!(
    isLoggedIn && travelAgency?.settings?.doShowUserLocalPreferences
  );

  return (
    <div className={clsx({
      'flex justify-between flex-wrap gap-y-4': doShowWithBrandingTopBar, 'flex lg:justify-between justify-center flex-wrap py-2': !doShowWithBrandingTopBar && !doShowAttributionBelowAgencyLogo, 'pb-2 pt-4': !doShowWithBranding, 'py-2': doShowWithBranding && !doShowAttributionBelowAgencyLogo, 'py-5 flex lg:justify-between justify-center flex-wrap': doShowAttributionBelowAgencyLogo,
    })}
    >
      {doShowWithBranding || doShowWithBrandingTopBar ? (
        <div className="flex flex-col">
          <AgencyLogoWithAttribution
            agencyLogoOverride={agencyLogoOverride}
          />
          {!doShowAffiliateTextToFooter && affiliateTextLine && (
            <div className="col-span-3 text-xs text-center mb-4 sm:mb-0">
              <MarkdownToHtml content={affiliateTextLine} className="text-black text-xs leading-tight" />
            </div>
          )}
        </div>
      ) : !doShowAttributionUnderSearchBar && (
        <div className="border-white  py-3 self-center col-span-3">
          <Link href="/">
            <a>
              <TravelWitsLogo />
            </a>
          </Link>
        </div>
      )}
      {agencyAdditionalLogo && doCenterAlignAdditionalLogo && (
        <div className="flex flex-col items-center mt-2 mb-2 sm:mt-0 sm:mb-0">
          <Link href="/">
            <a>
              <img
                className="h-24 sm:h-20"
                src={agencyAdditionalLogo}
                alt="secondary agency logo"
              />
            </a>
          </Link>
          {affiliateTextUnderAdditionalLogo && (
            <div className="text-xs mt-1 text-center">
              <MarkdownToHtml content={affiliateTextUnderAdditionalLogo} />
            </div>
          )}
        </div>
      )}
      <div className={clsx('text-right flex flex-wrap sm:flex-no-wrap flex-row-reverse col-span-8 md:col-span-5 md:whitespace-normal whitespace-no-wrap sm:gap-4', { 'gap-2 sm:justify-start': doShowWithBranding && !isLoading && !isLoggedIn, 'items-center': doShowWithBranding })}>
        {doShowUserLocalPreferences && <UserLocalPreferences />}
        <NavbarDropdown />
        <NavbarMenu navbar />
        {!doShowWithBranding && (
          <>
            {!doShowWithBrandingTopBar && (
              <Link href="/">
                <a className={`inline-block align-middle self-center w-7/12 sm:w-auto text-right ${isLoggedIn ? '' : 'py-3 mr-4'}`}>
                  <AgencyLogo className="align-middle inline-block" />
                </a>
              </Link>
            )}
            <CustomHeaderLinks travelAgencySettings={travelAgency?.settings} />
            <NavbarHelpDropdown />
            {!doAlignCurrencyFilterWithOtherFilters && <SelectCurrency />}
          </>
        )}
      </div>
    </div>
  );
};
Navbar.propTypes = {
  agencyLogoOverride: PropTypes.string,
};

Navbar.defaultProps = {
  agencyLogoOverride: undefined,
};
export default Navbar;
